import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { useHRAuth } from '../../contexts/HRAuthContext';
import axios from 'axios';
import {
    ArrowLeft, Users, Activity, BookOpen, Heart, Smile,
    TrendingUp, RefreshCw, BarChart3, Target,
    ThumbsUp, ThumbsDown, Minus, Filter, Leaf, Zap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NPS_COLOR = (score) => {
    if (score === null || score === undefined) return 'text-gray-400';
    if (score >= 50) return 'text-green-600';
    if (score >= 0) return 'text-yellow-600';
    return 'text-red-600';
};

const HRGroupDetail = () => {
    const { groupName } = useParams();
    const navigate = useNavigate();
    const { manager } = useHRAuth();
    const [activeTab, setActiveTab] = useState('overview');
    const [groupData, setGroupData] = useState(null);
    const [emotionalData, setEmotionalData] = useState(null);
    const [checkinData, setCheckinData] = useState(null);
    const [npsData, setNpsData] = useState(null);
    const [wellbeingData, setWellbeingData] = useState(null);
    const [empathyData, setEmpathyData] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [selectedDept, setSelectedDept] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const encoded = encodeURIComponent(groupName);
    const deptParam = selectedDept ? `?department=${encodeURIComponent(selectedDept)}` : '';

    const fetchAllData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [groupRes, emotionalRes, checkinRes, npsRes, wellbeingRes, empathyRes] = await Promise.all([
                axios.get(`${API}/hr/reports/group/${encoded}${deptParam}`, { withCredentials: true }),
                axios.get(`${API}/hr/reports/group/${encoded}/emotional${deptParam}`, { withCredentials: true }),
                axios.get(`${API}/hr/reports/group/${encoded}/checkin${deptParam}`, { withCredentials: true }),
                axios.get(`${API}/nps/reports/group/${encoded}/nps${deptParam}`, { withCredentials: true }),
                axios.get(`${API}/surveys/hr/reports/group/${encoded}/wellbeing${deptParam}`, { withCredentials: true }),
                axios.get(`${API}/surveys/hr/reports/group/${encoded}/empathy${deptParam}`, { withCredentials: true }),
            ]);
            setGroupData(groupRes.data);
            setEmotionalData(emotionalRes.data);
            setCheckinData(checkinRes.data);
            setNpsData(npsRes.data);
            setWellbeingData(wellbeingRes.data);
            setEmpathyData(empathyRes.data);
        } catch (err) {
            console.error('Error fetching data:', err);
        } finally {
            setIsLoading(false);
        }
    }, [encoded, deptParam]);

    useEffect(() => {
        axios.get(`${API}/hr/reports/group/${encoded}/departments`, { withCredentials: true })
            .then(r => setDepartments(r.data.departments || []))
            .catch(() => {});
    }, [encoded]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    const tabs = [
        { id: 'overview', label: 'Visão Geral', icon: BarChart3 },
        { id: 'learning', label: 'Aprendizado', icon: BookOpen },
        { id: 'emotional', label: 'Diário de Gatilhos', icon: Heart },
        { id: 'checkin', label: 'Check-in', icon: Smile },
        { id: 'nps', label: 'NPS', icon: ThumbsUp },
        { id: 'wellbeing', label: 'Bem-estar', icon: Leaf },
        { id: 'empathy', label: 'Empatia', icon: Zap },
    ];

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600">Carregando dados do grupo...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex flex-wrap justify-between items-center gap-4">
                        <div className="flex items-center space-x-4">
                            <Button onClick={() => navigate('/hr/dashboard')} variant="ghost" size="sm">
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Voltar
                            </Button>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">
                                    {decodeURIComponent(groupName)}
                                    {selectedDept && <span className="text-blue-600 ml-2">· {selectedDept}</span>}
                                </h1>
                                <p className="text-sm text-gray-500">{groupData?.total_users || 0} colaboradores</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {departments.length > 0 && (
                                <div className="flex items-center gap-2">
                                    <Filter className="w-4 h-4 text-gray-500" />
                                    <select
                                        value={selectedDept}
                                        onChange={e => setSelectedDept(e.target.value)}
                                        className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Todos os departamentos</option>
                                        {departments.map(d => (
                                            <option key={d} value={d}>{d}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <Button onClick={fetchAllData} variant="outline" size="sm">
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Atualizar
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Tabs */}
                <div className="flex space-x-2 mb-6 overflow-x-auto">
                    {tabs.map((tab) => (
                        <Button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            variant={activeTab === tab.id ? 'default' : 'outline'}
                            className={activeTab === tab.id ? 'bg-blue-600' : ''}
                        >
                            <tab.icon className="w-4 h-4 mr-2" />
                            {tab.label}
                        </Button>
                    ))}
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && groupData && (
                    <div className="space-y-6">
                        <div className="grid md:grid-cols-3 gap-4">
                            <Card className="p-6 bg-gradient-to-br from-green-50 to-white">
                                <div className="flex items-center space-x-3 mb-2">
                                    <Activity className="w-6 h-6 text-green-600" />
                                    <span className="text-gray-600">Ativos Hoje</span>
                                </div>
                                <p className="text-3xl font-bold text-green-600">{groupData.engagement?.active_today || 0}</p>
                                <p className="text-sm text-gray-500">{groupData.engagement?.active_today_pct || 0}% do grupo</p>
                            </Card>
                            <Card className="p-6 bg-gradient-to-br from-blue-50 to-white">
                                <div className="flex items-center space-x-3 mb-2">
                                    <TrendingUp className="w-6 h-6 text-blue-600" />
                                    <span className="text-gray-600">Ativos (7 dias)</span>
                                </div>
                                <p className="text-3xl font-bold text-blue-600">{groupData.engagement?.active_7d || 0}</p>
                                <p className="text-sm text-gray-500">{groupData.engagement?.active_7d_pct || 0}% do grupo</p>
                            </Card>
                            <Card className="p-6 bg-gradient-to-br from-purple-50 to-white">
                                <div className="flex items-center space-x-3 mb-2">
                                    <Users className="w-6 h-6 text-purple-600" />
                                    <span className="text-gray-600">Ativos (30 dias)</span>
                                </div>
                                <p className="text-3xl font-bold text-purple-600">{groupData.engagement?.active_30d || 0}</p>
                                <p className="text-sm text-gray-500">{groupData.engagement?.active_30d_pct || 0}% do grupo</p>
                            </Card>
                        </div>

                        <Card className="p-6">
                            <h3 className="font-bold text-gray-900 mb-4">Avaliação Inicial</h3>
                            <div className="flex items-center space-x-4">
                                <div className="flex-1 bg-gray-200 rounded-full h-4">
                                    <div className="bg-blue-600 h-4 rounded-full" style={{ width: `${groupData.assessment?.completion_rate || 0}%` }} />
                                </div>
                                <span className="font-bold text-blue-600">{groupData.assessment?.completion_rate || 0}%</span>
                            </div>
                            <p className="text-sm text-gray-500 mt-2">
                                {groupData.assessment?.completed || 0} de {groupData.total_users} colaboradores completaram
                            </p>
                        </Card>

                        <Card className="p-6">
                            <h3 className="font-bold text-gray-900 mb-4">Distribuição de XP</h3>
                            <div className="grid grid-cols-5 gap-2">
                                {groupData.xp_distribution && Object.entries(groupData.xp_distribution).map(([range, count]) => (
                                    <div key={range} className="text-center">
                                        <div className="bg-blue-100 rounded-lg p-3 mb-2">
                                            <p className="text-xl font-bold text-blue-600">{count}</p>
                                        </div>
                                        <p className="text-xs text-gray-600">{range}</p>
                                    </div>
                                ))}
                            </div>
                        </Card>

                        <Card className="p-6">
                            <h3 className="font-bold text-gray-900 mb-4">Distribuição de Streak</h3>
                            <div className="grid grid-cols-6 gap-2">
                                {groupData.streak_distribution && Object.entries(groupData.streak_distribution).map(([range, count]) => (
                                    <div key={range} className="text-center">
                                        <div className="bg-orange-100 rounded-lg p-3 mb-2">
                                            <p className="text-xl font-bold text-orange-600">{count}</p>
                                        </div>
                                        <p className="text-xs text-gray-600">{range} dias</p>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>
                )}

                {/* Learning Tab */}
                {activeTab === 'learning' && groupData && (
                    <div className="space-y-6">
                        <Card className="p-6">
                            <h3 className="font-bold text-gray-900 mb-4">Distribuição por Nível</h3>
                            <div className="space-y-3">
                                {groupData.level_distribution && Object.entries(groupData.level_distribution)
                                    .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                                    .map(([level, count]) => (
                                        <div key={level} className="flex items-center space-x-4">
                                            <span className="w-20 text-gray-600">Nível {level}</span>
                                            <div className="flex-1 bg-gray-200 rounded-full h-4">
                                                <div className="bg-purple-600 h-4 rounded-full" style={{ width: `${(count / groupData.total_users) * 100}%` }} />
                                            </div>
                                            <span className="w-16 text-right font-bold text-purple-600">
                                                {count} ({Math.round((count / groupData.total_users) * 100)}%)
                                            </span>
                                        </div>
                                    ))}
                            </div>
                        </Card>

                        <Card className="p-6">
                            <h3 className="font-bold text-gray-900 mb-4">Lições Completadas</h3>
                            <div className="space-y-3">
                                {groupData.lessons_distribution && Object.entries(groupData.lessons_distribution)
                                    .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                                    .slice(0, 10)
                                    .map(([lessons, count]) => (
                                        <div key={lessons} className="flex items-center space-x-4">
                                            <span className="w-24 text-gray-600">{lessons} lições</span>
                                            <div className="flex-1 bg-gray-200 rounded-full h-4">
                                                <div className="bg-green-600 h-4 rounded-full" style={{ width: `${(count / groupData.total_users) * 100}%` }} />
                                            </div>
                                            <span className="w-16 text-right font-bold text-green-600">{count}</span>
                                        </div>
                                    ))}
                            </div>
                        </Card>
                    </div>
                )}

                {/* Emotional Tab */}
                {activeTab === 'emotional' && emotionalData && (
                    <div className="space-y-6">
                        {emotionalData.total_entries === 0 ? (
                            <Card className="p-12 text-center">
                                <Heart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Sem registros ainda</h3>
                                <p className="text-gray-600">Os colaboradores ainda não registraram gatilhos emocionais nos últimos 30 dias.</p>
                            </Card>
                        ) : (
                            <>
                                <div className="grid md:grid-cols-4 gap-4">
                                    <Card className="p-6 bg-purple-50"><p className="text-gray-600 text-sm mb-1">Total de Registros</p><p className="text-2xl font-bold text-purple-600">{emotionalData.total_entries}</p></Card>
                                    <Card className="p-6 bg-blue-50"><p className="text-gray-600 text-sm mb-1">Usuários Ativos</p><p className="text-2xl font-bold text-blue-600">{emotionalData.users_with_entries}</p></Card>
                                    <Card className="p-6 bg-green-50"><p className="text-gray-600 text-sm mb-1">Taxa de Participação</p><p className="text-2xl font-bold text-green-600">{emotionalData.participation_rate}%</p></Card>
                                    <Card className="p-6 bg-orange-50"><p className="text-gray-600 text-sm mb-1">Média por Usuário</p><p className="text-2xl font-bold text-orange-600">{emotionalData.avg_entries_per_user}</p></Card>
                                </div>

                                <Card className="p-6">
                                    <h3 className="font-bold text-gray-900 mb-4">Emoções Mais Registradas</h3>
                                    <div className="space-y-3">
                                        {emotionalData.top_emotions?.slice(0, 8).map((emotion, index) => (
                                            <div key={index} className="flex items-center space-x-4">
                                                <span className="w-32 font-medium text-gray-700">{emotion.emotion}</span>
                                                <div className="flex-1 bg-gray-200 rounded-full h-4">
                                                    <div className="bg-purple-600 h-4 rounded-full" style={{ width: `${(emotion.count / emotionalData.total_entries) * 100}%` }} />
                                                </div>
                                                <span className="w-20 text-right text-gray-600">{emotion.count} ({emotion.avg_intensity}/10)</span>
                                            </div>
                                        ))}
                                    </div>
                                </Card>

                                <Card className="p-6">
                                    <h3 className="font-bold text-gray-900 mb-4">Fatores de Vulnerabilidade</h3>
                                    <p className="text-sm text-gray-500 mb-4">Porcentagem de registros onde cada fator foi reportado</p>
                                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {emotionalData.vulnerability_factors && Object.entries(emotionalData.vulnerability_factors).map(([factor, pct]) => {
                                            const labels = { sleep_deprived: 'Privação de Sono', hungry: 'Fome', overloaded: 'Sobrecarga', difficult_day: 'Dia Difícil', multitasking: 'Multitarefas' };
                                            return (
                                                <div key={factor} className="bg-orange-50 p-4 rounded-lg">
                                                    <p className="text-gray-700 mb-2">{labels[factor] || factor}</p>
                                                    <div className="flex items-center space-x-2">
                                                        <div className="flex-1 bg-orange-200 rounded-full h-2">
                                                            <div className="bg-orange-600 h-2 rounded-full" style={{ width: `${pct}%` }} />
                                                        </div>
                                                        <span className="font-bold text-orange-600">{pct}%</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Card>
                            </>
                        )}
                    </div>
                )}

                {/* Check-in Tab */}
                {activeTab === 'checkin' && checkinData && (
                    <div className="space-y-6">
                        {checkinData.total_checkins === 0 ? (
                            <Card className="p-12 text-center">
                                <Smile className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Sem check-ins ainda</h3>
                                <p className="text-gray-600">Os colaboradores ainda não realizaram check-ins nos últimos 30 dias.</p>
                            </Card>
                        ) : (
                            <>
                                <div className="grid md:grid-cols-4 gap-4">
                                    <Card className="p-6 bg-green-50"><p className="text-gray-600 text-sm mb-1">Total de Check-ins</p><p className="text-2xl font-bold text-green-600">{checkinData.total_checkins}</p></Card>
                                    <Card className="p-6 bg-blue-50"><p className="text-gray-600 text-sm mb-1">Usuários Participantes</p><p className="text-2xl font-bold text-blue-600">{checkinData.users_with_checkins}</p></Card>
                                    <Card className="p-6 bg-purple-50"><p className="text-gray-600 text-sm mb-1">Taxa de Participação</p><p className="text-2xl font-bold text-purple-600">{checkinData.participation_rate}%</p></Card>
                                    <Card className="p-6 bg-yellow-50"><p className="text-gray-600 text-sm mb-1">Energia Média</p><p className="text-2xl font-bold text-yellow-600">{checkinData.avg_energy_level || '—'}/5</p></Card>
                                </div>

                                <Card className="p-6">
                                    <h3 className="font-bold text-gray-900 mb-4">Distribuição de Humor</h3>
                                    <div className="grid md:grid-cols-5 gap-4">
                                        {checkinData.mood_distribution && Object.entries(checkinData.mood_distribution).map(([mood, pct]) => {
                                            const moodInfo = { 'great': { emoji: '😄', label: 'Ótimo', color: 'green' }, 'good': { emoji: '🙂', label: 'Bem', color: 'blue' }, 'okay': { emoji: '😐', label: 'Ok', color: 'yellow' }, 'not_great': { emoji: '😔', label: 'Não muito bem', color: 'orange' }, 'bad': { emoji: '😢', label: 'Ruim', color: 'red' } };
                                            const info = moodInfo[mood] || { emoji: '❓', label: mood, color: 'gray' };
                                            return (
                                                <div key={mood} className="bg-gray-50 p-4 rounded-lg text-center">
                                                    <div className="text-4xl mb-2">{info.emoji}</div>
                                                    <p className="font-bold text-gray-900">{pct}%</p>
                                                    <p className="text-sm text-gray-600">{info.label}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Card>
                            </>
                        )}
                    </div>
                )}

                {/* NPS Tab */}
                {activeTab === 'nps' && (
                    <div className="space-y-6">
                        {!npsData || npsData.overall?.total === 0 ? (
                            <Card className="p-12 text-center">
                                <ThumbsUp className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Sem respostas de NPS ainda</h3>
                                <p className="text-gray-600">O NPS é coletado após os colaboradores completarem módulos.</p>
                            </Card>
                        ) : (
                            <>
                                {/* Score cards */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <Card className="p-6 text-center">
                                        <p className="text-xs text-gray-500 mb-1">NPS Score</p>
                                        <p className={`text-4xl font-bold ${NPS_COLOR(npsData.overall.nps_score)}`}>
                                            {npsData.overall.nps_score >= 0 ? '+' : ''}{npsData.overall.nps_score}
                                        </p>
                                        <p className="text-xs text-gray-400 mt-1">{npsData.overall.total} respostas</p>
                                    </Card>
                                    <Card className="p-6 bg-green-50 text-center">
                                        <ThumbsUp className="w-5 h-5 text-green-600 mx-auto mb-1" />
                                        <p className="text-2xl font-bold text-green-600">{npsData.overall.promoters}</p>
                                        <p className="text-xs text-gray-500">Promotores</p>
                                        <p className="text-xs text-green-600">{npsData.overall.promoters_pct}%</p>
                                    </Card>
                                    <Card className="p-6 bg-yellow-50 text-center">
                                        <Minus className="w-5 h-5 text-yellow-600 mx-auto mb-1" />
                                        <p className="text-2xl font-bold text-yellow-600">{npsData.overall.neutrals}</p>
                                        <p className="text-xs text-gray-500">Neutros</p>
                                        <p className="text-xs text-yellow-600">{npsData.overall.neutrals_pct}%</p>
                                    </Card>
                                    <Card className="p-6 bg-red-50 text-center">
                                        <ThumbsDown className="w-5 h-5 text-red-600 mx-auto mb-1" />
                                        <p className="text-2xl font-bold text-red-600">{npsData.overall.detractors}</p>
                                        <p className="text-xs text-gray-500">Detratores</p>
                                        <p className="text-xs text-red-600">{npsData.overall.detractors_pct}%</p>
                                    </Card>
                                </div>

                                {/* Monthly trend */}
                                {npsData.monthly_trend?.length > 0 && (
                                    <Card className="p-6">
                                        <h3 className="font-bold text-gray-900 mb-4">Tendência Mensal de NPS</h3>
                                        <div className="flex items-end gap-3 h-32 bg-gray-50 rounded-lg p-4">
                                            {npsData.monthly_trend.map((m, i) => {
                                                const s = m.nps_score;
                                                const height = s === null ? 4 : Math.max(4, ((s + 100) / 200) * 80);
                                                const color = s === null ? 'bg-gray-200' : s >= 50 ? 'bg-green-500' : s >= 0 ? 'bg-yellow-500' : 'bg-red-500';
                                                return (
                                                    <div key={i} className="flex flex-col items-center flex-1">
                                                        <span className="text-xs text-gray-500 mb-1">{s !== null ? (s >= 0 ? '+' : '') + s : '—'}</span>
                                                        <div className={`w-full rounded-t ${color}`} style={{ height }} />
                                                        <span className="text-xs text-gray-400 mt-1">{m.month?.slice(5)}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Card>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Wellbeing Tab */}
                {activeTab === 'wellbeing' && (
                    <div className="space-y-6">
                        {!wellbeingData || wellbeingData.total_responses === 0 ? (
                            <Card className="p-12 text-center">
                                <Leaf className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Sem respostas ainda</h3>
                                <p className="text-gray-600">A pesquisa mensal de bem-estar é exibida automaticamente para os colaboradores.</p>
                            </Card>
                        ) : (
                            <>
                                <div className="grid md:grid-cols-3 gap-4">
                                    <Card className="p-6 bg-teal-50 text-center">
                                        <p className="text-xs text-gray-500 mb-1">Score Médio Geral</p>
                                        <p className={`text-4xl font-bold ${wellbeingData.overall_avg >= 70 ? 'text-teal-600' : wellbeingData.overall_avg >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                                            {wellbeingData.overall_avg}
                                        </p>
                                        <p className="text-xs text-gray-400 mt-1">/ 100</p>
                                    </Card>
                                    <Card className="p-6 bg-blue-50 text-center">
                                        <p className="text-xs text-gray-500 mb-1">Respostas coletadas</p>
                                        <p className="text-4xl font-bold text-blue-600">{wellbeingData.total_responses}</p>
                                    </Card>
                                    <Card className="p-6 bg-gray-50">
                                        <p className="text-xs text-gray-500 mb-2 font-medium">Por Departamento</p>
                                        {wellbeingData.by_department && Object.entries(wellbeingData.by_department).length > 0 ? (
                                            <div className="space-y-1">
                                                {Object.entries(wellbeingData.by_department).map(([dept, score]) => (
                                                    <div key={dept} className="flex justify-between items-center">
                                                        <span className="text-xs text-gray-600 truncate">{dept}</span>
                                                        <span className={`text-sm font-bold ml-2 ${score >= 70 ? 'text-teal-600' : score >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>{score}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : <p className="text-xs text-gray-400">Nenhum departamento.</p>}
                                    </Card>
                                </div>

                                {wellbeingData.monthly_trend?.length > 0 && (
                                    <Card className="p-6">
                                        <h3 className="font-bold text-gray-900 mb-4">Tendência Mensal de Bem-estar</h3>
                                        <div className="flex items-end gap-3 h-32 bg-gray-50 rounded-lg p-4">
                                            {wellbeingData.monthly_trend.map((m, i) => {
                                                const s = m.avg_score;
                                                const height = s === null ? 4 : Math.max(4, (s / 100) * 80);
                                                const color = s === null ? 'bg-gray-200' : s >= 70 ? 'bg-teal-500' : s >= 50 ? 'bg-yellow-500' : 'bg-red-400';
                                                return (
                                                    <div key={i} className="flex flex-col items-center flex-1">
                                                        <span className="text-xs text-gray-500 mb-1">{s !== null ? s : '—'}</span>
                                                        <div className={`w-full rounded-t ${color}`} style={{ height }} />
                                                        <span className="text-xs text-gray-400 mt-1">{m.month?.slice(5)}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <p className="text-xs text-gray-400 mt-2 text-center">Escala 0-100 · média mensal do grupo</p>
                                    </Card>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Empathy Tab */}
                {activeTab === 'empathy' && (
                    <div className="space-y-6">
                        {!empathyData || empathyData.total_responses === 0 ? (
                            <Card className="p-12 text-center">
                                <Zap className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Sem avaliações ainda</h3>
                                <p className="text-gray-600">A escala de empatia é aplicada em 3 momentos: baseline, meio do caminho e final.</p>
                            </Card>
                        ) : (
                            <>
                                {/* Level summary */}
                                <div className="grid md:grid-cols-3 gap-4">
                                    {['baseline', 'midpoint', 'final'].map((lvl) => {
                                        const d = empathyData.by_level?.[lvl];
                                        const labels = { baseline: 'Inicial', midpoint: 'Meio', final: 'Final' };
                                        return (
                                            <Card key={lvl} className={`p-6 text-center ${d ? 'bg-purple-50' : 'bg-gray-50'}`}>
                                                <p className="text-sm text-gray-500 mb-1">{labels[lvl]}</p>
                                                {d ? (
                                                    <>
                                                        <p className="text-4xl font-bold text-purple-600">{d.total}</p>
                                                        <p className="text-xs text-gray-400 mt-1">{d.count} respostas · /100</p>
                                                        <div className="mt-3 space-y-1 text-xs text-left">
                                                            <div className="flex justify-between"><span className="text-gray-500">🔭 Perspectiva</span><span className="font-semibold">{d.perspectiva}</span></div>
                                                            <div className="flex justify-between"><span className="text-gray-500">💗 Cuidado</span><span className="font-semibold">{d.cuidado}</span></div>
                                                            <div className="flex justify-between"><span className="text-gray-500">🌿 Presença</span><span className="font-semibold">{d.presenca}</span></div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <p className="text-2xl text-gray-300 mt-2">—</p>
                                                )}
                                            </Card>
                                        );
                                    })}
                                </div>

                                {/* Delta visualization */}
                                {empathyData.by_level?.baseline && (
                                    <Card className="p-6">
                                        <h3 className="font-bold text-gray-900 mb-4">Evolução por Dimensão</h3>
                                        {['perspectiva', 'cuidado', 'presenca'].map((dim) => {
                                            const dimLabels = { perspectiva: '🔭 Perspectiva', cuidado: '💗 Cuidado Compassivo', presenca: '🌿 Presença Empática' };
                                            const scores = ['baseline', 'midpoint', 'final'].map(l => empathyData.by_level?.[l]?.[dim] ?? null);
                                            return (
                                                <div key={dim} className="mb-4">
                                                    <p className="text-sm font-medium text-gray-700 mb-2">{dimLabels[dim]}</p>
                                                    <div className="flex gap-3 items-end h-16">
                                                        {['Inicial', 'Meio', 'Final'].map((lbl, i) => {
                                                            const s = scores[i];
                                                            const h = s === null ? 4 : Math.max(4, (s / 100) * 56);
                                                            return (
                                                                <div key={lbl} className="flex flex-col items-center w-16">
                                                                    <span className="text-xs text-gray-500 mb-0.5">{s !== null ? s : '—'}</span>
                                                                    <div className={`w-full rounded-t ${s === null ? 'bg-gray-200' : 'bg-purple-400'}`} style={{ height: h }} />
                                                                    <span className="text-xs text-gray-400 mt-0.5">{lbl}</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </Card>
                                )}
                            </>
                        )}
                    </div>
                )}

                <div className="mt-8 text-center text-sm text-gray-500">
                    <p>Dados anonimizados — privacidade dos colaboradores protegida</p>
                </div>
            </div>
        </div>
    );
};

export default HRGroupDetail;
